import { Component, OnInit, Input, Output, ViewChild, EventEmitter, Inject, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit, ElementRef, OnDestroy, NgZone } from '@angular/core';
import { StoreMenuItemsV2, MenuItemGroup, Store } from 'src/app/models/store';
import { ActivatedRoute } from '@angular/router';
import { OrdersWithPayment } from 'src/app/models/order';
import { OrderService } from 'src/app/order.service';
import { MatAccordion, MatExpansionPanel } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { MenuItemBottomSheetComponent, MenuItemBottomSheetData } from 'src/app/menu-item/menu-item-bottom-sheet/menu-item-bottom-sheet.component';

@Component({
  selector: 'store-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuComponent implements OnInit, AfterViewInit, OnDestroy {

  displayedColumns: string[] = ['picture', 'item', 'price'];
  @Input() orderId: string;
  @Input() storeMenu: StoreMenuItemsV2;
  @Input() store: Store;
  @Input() orderWithPayment: OrdersWithPayment;
  @Input() filter: string;
  @Input() openAll;
  @Input() isCatering: boolean = false;
  @Input() searchScrolledOut: boolean = false;
  @Output() emitLazy
  @Output() orderUpdated = new EventEmitter<void>();
  @Output() filterChanged = new EventEmitter<string>();

  @ViewChild('categorySidebar') categorySidebarRef: ElementRef;

  isEnd = false
  limit = 50
  offset = 0
  selectedCategoryFragment;
  selectedSubCategoryFragment;
  uiSetting: any;
  activeCategory: string = '';
  activeSubCategory: string = '';
  isDesktop = false;
  categorySelected = false;
  categoryDropdownOpen = false;
  headerOffset = 72; // dynamically calculated in ngAfterViewInit
  categoryImages: { [key: string]: string } = {};
  subCategoryImages: { [key: string]: string } = {};
  expandedCategories = new Set<string>();
  expandedSubCategories = new Set<string>();
  readonly imageBase = 'https://storage.googleapis.com/menus/';

  private scrollTicking = false;
  private isScrollSpyEnabled = true;
  private desktopQuery: MediaQueryList;

  constructor(
    private orderService: OrderService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    @Inject(DOCUMENT) private document,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private overlay: Overlay
  ) { }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler($event: any): void {
    localStorage.removeItem('menu_group_name');
    localStorage.removeItem('menu_sub_group_name');
  }

  ngOnInit() {
    this.uiSetting = this.store.ui_settings;
    this.route.fragment.subscribe(fragment => {
      this.selectedCategoryFragment = fragment;
      this.cdr.markForCheck();
    });
    for (let key in this.storeMenu.menu_items) {
      let value = this.storeMenu.menu_items[key];
      value.setCurrentOffset = 0
      value.disabled = false;
    }

    /* Click Back Behaviour */
    const selectedMenuGroup = localStorage.getItem('menu_group_name');
    const selectedSubMenuGroup = localStorage.getItem('menu_sub_group_name');
    if (selectedMenuGroup) {
      this.selectedCategoryFragment = selectedMenuGroup;
      if (selectedSubMenuGroup) {
        this.selectedSubCategoryFragment = selectedSubMenuGroup
      } else {
        this.selectedSubCategoryFragment = null;
      }
    } else {
      this.selectedCategoryFragment = null;
    }

    // Initialize expanded state from feature flags
    if (this.storeMenu?.menu_order) {
      const expandCat = this.store.isEnabled('collapse_menu_category');
      const expandSub = this.store.isEnabled('collapse_menu_subcategory');
      for (const itemGroup of this.storeMenu.menu_order) {
        if (expandCat) {
          this.expandedCategories.add(itemGroup[0]);
        }
        if (expandSub && itemGroup[1]) {
          for (const sub of itemGroup[1]) {
            if (sub) this.expandedSubCategories.add(this.getSubCategoryId(itemGroup[0], sub));
          }
        }
      }
    }

    // Build category image map from menu items
    this.buildCategoryImages();

    // Initialize active category to first one
    if (this.storeMenu?.menu_order?.length) {
      this.activeCategory = this.storeMenu.menu_order[0][0];
    }

    // Auto-select if single category or expand-by-default is enabled (skip browse screen)
    if (!this.storeMenu?.menu_order || this.storeMenu.menu_order.length <= 1 || this.store.isEnabled('collapse_menu_category')) {
      this.categorySelected = true;
    }

    // Desktop detection
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.isDesktop = this.desktopQuery.matches;
    if (this.isDesktop) this.categorySelected = true;
    this.desktopQuery.addEventListener('change', this.onDesktopChange);
  }

  ngAfterViewInit() {
    // Measure actual sticky header height to position sidebar & scroll offsets correctly
    this.calculateHeaderOffset();

    // Run scroll listener outside Angular zone for performance
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    });
  }

  private calculateHeaderOffset() {
    // Delay one tick to ensure header is rendered (logo may load async)
    setTimeout(() => {
      const header = this.document.querySelector('.store-header-toolbar') as HTMLElement;
      if (header) {
        this.headerOffset = header.getBoundingClientRect().height;
        this.cdr.markForCheck();
      }
      // Recalculate after logo might have loaded
      setTimeout(() => {
        if (header) {
          const newHeight = header.getBoundingClientRect().height;
          if (newHeight !== this.headerOffset) {
            this.headerOffset = newHeight;
            this.cdr.markForCheck();
          }
        }
      }, 1000);
    });
  }

  ngOnDestroy() {
    window.removeEventListener('scroll', this.onWindowScroll);
    this.desktopQuery?.removeEventListener('change', this.onDesktopChange);
  }

  private onDesktopChange = (e: MediaQueryListEvent) => {
    this.isDesktop = e.matches;
    if (this.isDesktop) this.categorySelected = true;
    this.cdr.markForCheck();
  }

  private onWindowScroll = () => {
    if (!this.scrollTicking && this.isScrollSpyEnabled) {
      requestAnimationFrame(() => {
        this.updateActiveCategory();
        this.scrollTicking = false;
      });
      this.scrollTicking = true;
    }
  }

  private updateActiveCategory() {
    if (!this.storeMenu?.menu_order?.length) return;

    // Desktop: use measured header offset; Mobile: sticky search + sticky select (~106px)
    const scrollOffset = this.isDesktop ? this.headerOffset : 110;
    let closestCategory = this.storeMenu.menu_order[0][0];
    let closestDistance = Infinity;

    for (const itemGroup of this.storeMenu.menu_order) {
      const categoryName = itemGroup[0];
      const el = this.document.querySelector(`[id="${categoryName}"]`) as HTMLElement;
      if (el) {
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top - scrollOffset);
        if (rect.top <= scrollOffset + 10 && distance < closestDistance) {
          closestDistance = distance;
          closestCategory = categoryName;
        }
      }
    }

    // Track active sub-category within the active parent
    let newActiveSub = '';
    const activeGroup = this.storeMenu.menu_order.find(g => g[0] === closestCategory);
    if (activeGroup && this.hasSubCategories(activeGroup)) {
      let closestSubDist = Infinity;
      for (const sub of activeGroup[1]) {
        if (!sub) continue;
        const subId = this.getSubCategoryId(closestCategory, sub);
        const el = this.document.querySelector(`[id="${subId}"]`) as HTMLElement;
        if (el) {
          const rect = el.getBoundingClientRect();
          const distance = Math.abs(rect.top - scrollOffset);
          if (rect.top <= scrollOffset + 10 && distance < closestSubDist) {
            closestSubDist = distance;
            newActiveSub = sub;
          }
        }
      }
    }

    if (closestCategory !== this.activeCategory || newActiveSub !== this.activeSubCategory) {
      this.activeCategory = closestCategory;
      this.activeSubCategory = newActiveSub;
      this.scrollPillIntoView(closestCategory);
      this.ngZone.run(() => {
        this.cdr.markForCheck();
      });
    }
  }

  private scrollPillIntoView(categoryName: string) {
    if (this.isDesktop && this.categorySidebarRef) {
      const sidebar = this.categorySidebarRef.nativeElement as HTMLElement;
      const item = sidebar.querySelector(`[data-category="${categoryName}"]`) as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
    // Mobile: select value updates automatically via [selected] binding
  }

  selectCategory(categoryName: string) {
    this.categorySelected = true;
    this.activeCategory = categoryName;
    this.activeSubCategory = '';
    this.expandedCategories.add(categoryName);
    this.cdr.markForCheck();
    // Wait for menu items to render, then scroll
    setTimeout(() => {
      this.scrollToCategory(categoryName);
    }, 150);
  }

  scrollToCategory(categoryName: string) {
    this.isScrollSpyEnabled = false;
    this.activeCategory = categoryName;
    this.activeSubCategory = '';
    this.selectedCategoryFragment = categoryName;
    this.expandedCategories.add(categoryName);
    this.cdr.markForCheck();

    const delay = this.isDesktop ? 0 : 100;
    setTimeout(() => {
      const el = this.document.querySelector(`[id="${categoryName}"]`) as HTMLElement;
      if (el) {
        const offset = this.isDesktop ? this.headerOffset : 110;
        el.style.scrollMarginTop = offset + 'px';
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => {
        this.isScrollSpyEnabled = true;
      }, 500);
    }, delay);
  }

  openItemBottomSheet(item: any) {
    const sheetData: MenuItemBottomSheetData = {
      storeId: this.store.id,
      orderId: this.orderId,
      menuItemGroupId: item.id,
      store: this.store,
      isCatering: this.isCatering,
    };

    const ref = this.dialog.open(MenuItemBottomSheetComponent, {
      panelClass: 'menu-item-fullscreen-dialog',
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      data: sheetData,
      scrollStrategy: this.overlay.scrollStrategies.noop()
    });

    ref.afterClosed().subscribe(result => {
      if (result?.success) {
        this.orderUpdated.emit();
      }
    });
  }

  onChangeMenu(e) {
    this.selectedCategoryFragment = e;
    this.selectedSubCategoryFragment = null;
    localStorage.removeItem('menu_group_name');
    localStorage.removeItem('menu_sub_group_name');
  }

  onChangeSubMenu(e) {
    this.selectedSubCategoryFragment = e;
    localStorage.removeItem('menu_sub_group_name');
  }

  disabledLoadMore(itemGroup) {
    return this.storeMenu.menu_items[itemGroup].disabled
  }

  loadMore(itemGroup, subgroup) {

    this.storeMenu.menu_items[itemGroup].setCurrentOffset = this.storeMenu.menu_items[itemGroup].setCurrentOffset + this.limit;
    let menu_group_id = this.storeMenu.menu_items[itemGroup][subgroup][0]['menu_group_id']
    this.orderService.getStoreMenuV2LazyIncremental(this.store.id, this.filter, this.limit.toString(),
      this.storeMenu.menu_items[itemGroup].setCurrentOffset.toString(), menu_group_id, itemGroup, subgroup).subscribe(data => {

        if (data.length < this.limit) {
          this.storeMenu.menu_items[itemGroup].disabled = true
        }
        this.storeMenu.menu_items[itemGroup][subgroup] = this.storeMenu.menu_items[itemGroup][subgroup].concat(data)
      })
  }

  hasSubCategories(itemGroup: any): boolean {
    return itemGroup[1]?.length > 1 || (itemGroup[1]?.length === 1 && !!itemGroup[1][0]);
  }

  getSubCategoryId(category: string, subCategory: string): string {
    return `${category}___${subCategory}`;
  }

  getVisibleSubCategories(itemGroup: any): string[] {
    if (!this.hasSubCategories(itemGroup)) return [];
    return (itemGroup[1] || []).filter(sub => !!sub);
  }

  scrollToSubCategory(category: string, subCategory: string) {
    this.isScrollSpyEnabled = false;
    this.activeCategory = category;
    this.activeSubCategory = subCategory;
    this.selectedCategoryFragment = category;
    this.selectedSubCategoryFragment = subCategory;
    this.expandedCategories.add(category);
    this.expandedSubCategories.add(this.getSubCategoryId(category, subCategory));
    this.cdr.markForCheck();

    setTimeout(() => {
      const subId = this.getSubCategoryId(category, subCategory);
      const el = this.document.querySelector(`[id="${subId}"]`) as HTMLElement;
      if (el) {
        const offset = this.isDesktop ? this.headerOffset : 110;
        el.style.scrollMarginTop = offset + 'px';
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setTimeout(() => {
        this.isScrollSpyEnabled = true;
      }, 500);
    }, 100);
  }

  onSidebarSearch(value: string) {
    this.filterChanged.emit(value);
  }

  private buildCategoryImages() {
    if (!this.storeMenu?.menu_items) return;
    for (const catName in this.storeMenu.menu_items) {
      const catData = this.storeMenu.menu_items[catName];
      let catImageSet = false;
      for (const subName in catData) {
        const items = catData[subName];
        if (Array.isArray(items) && items.length && items[0]?.image) {
          if (!catImageSet) {
            this.categoryImages[catName] = this.imageBase + items[0].image;
            catImageSet = true;
          }
          const subKey = this.getSubCategoryId(catName, subName);
          this.subCategoryImages[subKey] = this.imageBase + items[0].image;
        }
      }
    }
  }

  public santize(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

}
